import { z } from "zod";
import { NOTIFICATION_PREFERENCE_CATEGORIES } from "./notification-config.js";

/**
 * Shared DTO cho Notification Center — slice READ in-app (US-E3.F3-0017 / @UC0008).
 *
 * Một nguồn sự thật cho api (S01 — list + mark-read) và mobile (S02 — render),
 * tránh drift shape (DRY). C5: validate tại biên tái dùng được.
 *
 * Cross-check API_BUILD_PLAN [đã xoá → TDD §26] Phase 8.5: đây CHỈ là phần đọc/đánh-dấu in-app notifications
 * (GET /v1/notifications + PATCH read-all + PATCH :id/read) — KHÔNG dispatcher/worker/preferences.
 * Plan bất biến: `notifications.title/body` là snapshot đã render; channel `in_app` luôn lưu.
 *
 * BẢO MẬT (invariants):
 * - C2/C3: item CHỈ field cần cho UI; `.strict()` chặn userId/secret/field lạ lọt ra response.
 * - PII: notification của CHÍNH user (server lọc theo token); contract không mang field cross-user.
 * - Cursor opaque string — đối xứng common/pagination/cursor (envelope {data, pageInfo}).
 */

/** Max page-size. Default (20) do backend áp khi vắng. */
export const NOTIFICATIONS_MAX_LIMIT = 50;

/** Độ dài tối đa của cursor opaque tại biên (chặn input phình to). */
export const NOTIFICATIONS_MAX_QUERY_LENGTH = 512;

/**
 * 🔴 TRỤC THỨ HAI, CÓ CHỦ ĐÍCH — *"tin nằm ở tab nào của hộp thư"* (US-E24.F53-0510-S02 · @UC0008).
 *
 * Khai lại ở contracts thay vì import `@goppy/domain` vì **contracts không dep domain** (worker dep
 * domain, tầng HTTP validate bằng contracts; `apps/api` là surface duy nhất thấy cả hai). Đúng khuôn
 * đã dùng cho `NOTIFICATION_PREFERENCE_CATEGORIES`, và parity hai bản do
 * `apps/api/src/modules/notifications/notification-category-parity.spec.ts` canh.
 */
/**
 * 🔴 QĐ người dùng 25/08/2026 (US-E24.F53-0538) — GỘP `payment` vào `reminder`. Còn HAI tab.
 *
 * ⚠️ Client CŨ còn gửi `?tab=payment` sẽ ăn 400 ở biên (`.enum` + `.strict()`). Đó là hành vi ĐÚNG:
 * trả về một tab không còn tồn tại thì hộp thư hiện rỗng mà không ai biết vì sao — 400 ồn ào một
 * lần vẫn hơn một màn hình trắng im lặng.
 */
export const NOTIFICATION_TAB_VALUES = ["system", "reminder"] as const;
export type NotificationTabValue = (typeof NOTIFICATION_TAB_VALUES)[number];
export const notificationTabSchema = z.enum(NOTIFICATION_TAB_VALUES);

/**
 * 🔴 US-E24.F55-0516-S01 (@UC0008 @UC9518) — ĐÍCH ĐIỀU HƯỚNG khi người dùng bấm vào một tin.
 *
 * ═══ VÌ SAO SERVER TÍNH, KHÔNG PHẢI CLIENT SUY ═══
 * QĐ người dùng 21/08/2026. Client suy đích từ `type` nghĩa là bản đồ `type → màn` tồn tại ở **hai
 * nơi** (server biết ngữ nghĩa sự kiện, client biết màn), và hai bản đồ sẽ lệch nhau vào đúng lúc
 * thêm một loại tin mới — lúc không ai nhớ phải sửa cả hai.
 *
 * ═══ VÌ SAO `groupId` LÀ BẮT BUỘC ═══
 * Đo 21/08: **mọi** màn chi tiết của `apps/mobile` lồng dưới `:groupId`
 * (`/groups/:groupId/collections/:collectionId`, `/groups/:groupId/activities/:activityId`).
 * Thiếu nó thì client có `screen` mà vẫn **không dựng nổi đường dẫn** — đúng tình trạng 8/12 loại
 * tin đang hiện ở hộp thư mắc phải trước task này, và là lý do thật khiến tab đó còn chạy mock.
 *
 * ═══ ENUM ĐÓNG, HAI CHIỀU ═══
 * Server thêm màn mới mà client cũ chưa biết ⇒ client bỏ qua đích đó, mở hộp thư — **không nổ**.
 * Client nhận giá trị rác ⇒ chặn ở biên (400), 0 màn hình trắng.
 */
export const NOTIFICATION_TARGET_SCREENS = [
  /** `/groups/:groupId` */
  "groupDetail",
  /** `/groups/:groupId/collections/:collectionId` */
  "collectionDetail",
  /** `/groups/:groupId/activities/:activityId` */
  "activityDetail",
  /**
   * 🔴 US-NOTIF-0750 (@UC0051 @UC0070) — `/groups/:groupId/payment-claims/:memberDueId`, MÀN DUYỆT
   * lời khai nộp tiền.
   *
   * Id ở đây là **`memberDueId`**, không phải `collectionId`: đây là màn của MỘT khoản, và
   * `requiredTargetIdKey` (bên `@goppy/domain`) là chỗ khai điều đó — producer không phải đoán.
   *
   * Vì sao tách khỏi `collectionDetail`: tin *"có khoản nộp chờ bạn duyệt"* mở ra một HÀNH ĐỘNG
   * (duyệt/từ chối tiền), còn màn đợt thu chỉ là danh sách. Đưa người duyệt vào danh sách rồi để họ
   * tự dò tìm đúng dòng là bỏ dở đúng nửa việc mà cái tin sinh ra để làm.
   */
  "paymentApproval",
  /**
   * US-E23.F1-0783 (@UC9514 @UC0070) — bản xuất báo cáo xong/hỏng. Id là **`reportId`**; mobile xin
   * link tải LÚC CHẠM qua `GET /groups/:groupId/reports/:reportId` (link ký không nằm trong tin).
   */
  "reportDownload",
  /**
   * 🔴 US-E6.F3-0913 (@UC0057 @UC0051) — `/groups/:groupId/payment-confirmations/:confirmationId`, màn LỜI KHAI đọc theo id.
   * Lời khai đóng góp "Quỹ chung" không có khoản kỳ thu ⇒ không mở được `paymentApproval` (id là `memberDueId`). Id ở đây là
   * **`confirmationId`** (`requiredTargetIdKey` bên `@goppy/domain`).
   */
  "paymentClaim",
] as const;
export type NotificationTargetScreen = (typeof NOTIFICATION_TARGET_SCREENS)[number];

/**
 * 🔴 Tab mở sẵn của `groupDetail` (người dùng báo 27/10/2026: tin "có người xin vào nhóm" mở tab Hoạt động thay vì Thành viên).
 * TUỲ CHỌN và chỉ đi với `groupDetail` — app bản cũ bỏ qua trường lạ và vẫn mở nhóm như trước. Gương: `NotificationTargetTab` bên
 * `@goppy/domain`.
 */
export const NOTIFICATION_TARGET_TABS = ["members"] as const;
export type NotificationTargetTab = (typeof NOTIFICATION_TARGET_TABS)[number];

/** Màn CHI TIẾT — đòi thêm `id`. Tách ra thành hằng để luật ở một chỗ, không rải theo nhánh `if`. */
export const NOTIFICATION_TARGET_SCREENS_NEEDING_ID: ReadonlyArray<NotificationTargetScreen> = [
  "collectionDetail",
  "activityDetail",
  "paymentApproval",
  "reportDownload",
  "paymentClaim",
];

export const notificationTargetSchema = z
  .object({
    screen: z.enum(NOTIFICATION_TARGET_SCREENS),
    /** BẮT BUỘC — xem docblock: mọi màn đích đều lồng dưới `:groupId`. */
    groupId: z.string().uuid(),
    /** Id của thứ được mở (đợt thu / hoạt động). Vắng ở `groupDetail`. */
    id: z.string().uuid().optional(),
    /** Tab mở sẵn — CHỈ với `groupDetail` (refine dưới). */
    tab: z.enum(NOTIFICATION_TARGET_TABS).optional(),
  })
  .strict()
  .refine((t) => t.tab === undefined || t.screen === "groupDetail", {
    message: "`tab` chỉ đi với `groupDetail`",
    path: ["tab"],
  })
  .refine(
    (t) => !NOTIFICATION_TARGET_SCREENS_NEEDING_ID.includes(t.screen) || t.id !== undefined,
    {
      // Fail-closed ở BIÊN: một `collectionDetail` thiếu `id` mà lọt ra sẽ thành đường dẫn
      // `/groups/x/collections/undefined` — màn lỗi, và lỗi đó chỉ lộ ở tay người dùng.
      message: "màn chi tiết bắt buộc có `id`",
      path: ["id"],
    },
  );

export type NotificationTarget = z.infer<typeof notificationTargetSchema>;

/**
 * 🔴 US-E24.F56-0519-S01 (@UC0008 @UC0060) — CHÚ THÍCH CHÂN THẺ: chồng avatar + *"N thành viên vẫn
 * chưa đóng quỹ"* (Figma "Notifications Card New", node 201:18545).
 *
 * ═══ HAI FIELD, HAI LUẬT RIÊNG BIỆT — ĐỌC KỸ TRƯỚC KHI SỬA ═══
 * Chúng trông như một cặp nhưng đứng ở hai bên của ranh giới riêng tư mà repo này ĐÃ vẽ sẵn
 * (CLAUDE.md §7 P5.3 — *"tách bạch hai kênh của chuyện ai chưa đóng"*):
 *
 * · **`pendingMemberCount` = SỐ ĐẾM.** Cùng hạng với `collectionPeriodStatusResponseSchema`
 *   (`totalMembers`/`paidMembers`), vốn đã mở cho **mọi member ACTIVE** từ US-E6.F8-0046 với ghi chú
 *   *"response là AGGREGATE-ONLY (đếm + %)"* và **không** `@RequireFundRoles`. Con số này trừ nhau ra
 *   được từ endpoint đó, nên giấu ở đây là giấu một thứ đang công khai ở cửa bên cạnh.
 *
 * · **`actors` = DANH TÍNH.** Cùng hạng với `GET /collections/:id/members-status`, và bị gác bởi
 *   `payment_status_visibility` (mặc định hệ thống nay `all_members` — QĐ người dùng 07/09/2026;
 *   **fail-closed** `managers_only` chỉ khi đọc config hỏng). Server trả `[]` khi người
 *   đọc không được phép — KHÔNG phải khi không có ai chưa đóng. Hai ca đó phân biệt bằng
 *   `pendingMemberCount`, không phải bằng `actors.length`.
 *
 * ⇒ Bởi vậy **KHÔNG được suy con số từ `actors.length`**: danh sách bị cắt cho vừa chỗ VÀ bị giấu
 * theo config, còn con số là sự thật server đếm trên toàn đợt thu.
 */
export const NOTIFICATION_ACTORS_MAX = 3;

export const notificationActorSchema = z
  .object({
    /** `groupMemberId` — KHÔNG phải `userId`. Thẻ không cho bấm vào một khuôn mặt, nên id chỉ để `key`. */
    id: z.string().uuid(),
    /** Nickname trong nhóm, hoặc tên thật khi chưa đặt nickname. Rỗng ⇒ client tự dựng chữ viết tắt. */
    displayName: z.string(),
    /** URL đã resolve từ object key. `null` ⇒ vẽ chữ viết tắt; KHÔNG bịa ảnh mặc định. */
    avatarUrl: z.string().nullable(),
  })
  .strict();

export type NotificationActor = z.infer<typeof notificationActorSchema>;

/**
 * Một notification in-app (UC0008). title/body/data NULLABLE (snapshot có thể trống).
 * readAt = ISO string khi đã đọc, null khi chưa đọc. `.strict()` chặn userId/secret.
 */
export const notificationItemSchema = z
  .object({
    id: z.string().uuid(),
    type: z.string(),
    title: z.string().nullable(),
    body: z.string().nullable(),
    // ISO-8601 string khi serialize JSON.
    createdAt: z.string(),
    // null = chưa đọc; ISO string = đã đọc.
    readAt: z.string().nullable(),
    // Payload điều hướng (vd { groupId }) — nullable; KHÔNG chứa PII thô / secret.
    data: z.unknown().nullable(),
    // US-E23.F4-0334-S09 (@UC9519): null = chưa lưu trữ. KHÔNG có `doneAt` — "việc đã xong" là câu
    // hỏi về THẾ GIỚI, dẫn xuất qua `GET /me/action-items`, không phải cờ đóng băng ở đây.
    archivedAt: z.string().nullable(),
    // 🔴 US-E24.F53-0510-S02 (@UC0008) — BẮT BUỘC, không optional: server LUÔN biết tab của tin nó
    // trả về (suy từ `type` qua `tabForEvent`). Để optional thì client không phân biệt được "tin
    // không thuộc tab nào" với "server cũ chưa biết field này" — và ca thứ nhất không tồn tại, vì
    // API chỉ trả tin thuộc 3 tab.
    tab: notificationTabSchema,
    // 🔴 US-E24.F55-0516-S01 (@UC0008) — BẮT BUỘC nhưng NULLABLE, không optional.
    //
    // `null` = tin này không có đích cụ thể, mở hộp thư là đủ. Để `optional` thì client không phân
    // biệt được "không có đích" với "server cũ chưa biết field này" — và ca thứ hai sẽ đẩy client
    // vào chỗ phải đoán, đúng thứ task này sinh ra để dẹp.
    target: notificationTargetSchema.nullable(),
    // 🔴 US-E24.F56-0519-S01 — BẮT BUỘC, mặc định `[]`. Xem docblock `notificationActorSchema` ở
    // trên: `[]` nghĩa là *"không có khuôn mặt nào để vẽ"* và **gộp hai lý do** (không ai chưa đóng /
    // người đọc không được xem danh tính). Đó là CHỦ ĐÍCH — client không cần biết mình đang bị giấu,
    // và nói ra sẽ là một kênh rò rỉ nhỏ ("nhóm này có bật công khai hay không").
    actors: z.array(notificationActorSchema).max(NOTIFICATION_ACTORS_MAX),
    // 🔴 BẮT BUỘC nhưng NULLABLE. `null` = thẻ này KHÔNG có chú thích chân (tin không nói về một đợt
    // thu). Khác hẳn `0` = *"đợt thu này mọi người đã đóng hết"* — một sự thật đáng hiển thị.
    // Gộp hai thứ vào `0` là mất một câu có nghĩa.
    pendingMemberCount: z.number().int().min(0).nullable(),
  })
  .strict();

export type NotificationItem = z.infer<typeof notificationItemSchema>;

/**
 * US-E23.F4-0334-S07 (@UC9518) — trạng thái lọc hộp thư. Ba tab Figma map lên đây.
 *
 * 🔴 `archived` TÁCH HẲN khỏi `unread|read`: đã lưu trữ thì KHÔNG xuất hiện ở hai tab kia nữa
 * (AC1), nên đây là ba tập RỜI NHAU chứ không phải ba bộ lọc chồng lên nhau.
 */
export const NOTIFICATION_STATUS_FILTER = {
  UNREAD: "unread",
  READ: "read",
  ARCHIVED: "archived",
} as const;
export type NotificationStatusFilter =
  (typeof NOTIFICATION_STATUS_FILTER)[keyof typeof NOTIFICATION_STATUS_FILTER];
export const notificationStatusFilterSchema = z.enum([
  NOTIFICATION_STATUS_FILTER.UNREAD,
  NOTIFICATION_STATUS_FILTER.READ,
  NOTIFICATION_STATUS_FILTER.ARCHIVED,
]);

/**
 * Bộ 6 category của `NotificationPreference` — trục *"người dùng có muốn nhận không"* (bật/tắt push).
 *
 * ⚠️ Docblock cũ ở đây viết *"3 chip Figma là NHÃN GỘP map lên bộ này, đừng khai enum 3 giá trị
 * riêng"*. Câu đó **đã sai** — đo 20/08/2026: 3 tin của chip *Hệ thống*
 * (`group.join_request.approved` · `.rejected` · `group.suspended`) có trong `EVENT_REGISTRY` nhưng
 * **không có category nào**, nên chip không suy được từ bộ này. Xem `notificationTabSchema` ngay dưới.
 */
export const notificationCategoryFilterSchema = z.enum(NOTIFICATION_PREFERENCE_CATEGORIES);

/**
 * Query list: cursor + limit + lọc theo **tab** (hộp thư) HOẶC **category** (nhóm preference) + status.
 *
 * 🔴 `tab` và `category` LOẠI TRỪ NHAU. Chúng hỏi hai chuyện khác nhau, nên gửi cả hai là một trạng
 * thái không có nghĩa. Từ chối ở biên thay vì chọn ngầm một bên rồi im lặng bỏ qua bên kia — client
 * gửi nhầm sẽ biết ngay, không phải đi tìm xem vì sao bộ lọc "không ăn".
 */
export const notificationsListQuerySchema = z
  .object({
    cursor: z.string().max(NOTIFICATIONS_MAX_QUERY_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(NOTIFICATIONS_MAX_LIMIT).optional(),
    category: notificationCategoryFilterSchema.optional(),
    tab: notificationTabSchema.optional(),
    status: notificationStatusFilterSchema.optional(),
  })
  .refine((q) => !(q.tab !== undefined && q.category !== undefined), {
    message: "Không dùng đồng thời `tab` và `category` — hai trục lọc độc lập.",
    path: ["tab"],
  });

export type NotificationsListQuery = z.infer<typeof notificationsListQuerySchema>;

/**
 * Envelope list khớp quy ước repo `{ data, pageInfo }` + `unreadCount` cho bell badge.
 * unreadCount = số notification chưa đọc của user (≥0) — nguồn cho badge biến mất sau read-all.
 */
export const notificationsListResponseSchema = z.object({
  data: z.array(notificationItemSchema),
  pageInfo: z.object({
    hasNext: z.boolean(),
    nextCursor: z.string().nullable(),
  }),
  unreadCount: z.number().int().min(0),
  // 🔴 US-E24.F53-0510-S02 — badge từng tab. `unreadCount` ĐỊNH NGHĨA LẠI = tổng các số này (trước
  // đây nó đếm CẢ hộp thư gồm tin không hiện ở tab nào ⇒ badge nói 5 mà các tab cộng lại ra 2).
  //
  // 🔴 US-E24.F53-0538 — dựng TỪ `NOTIFICATION_TAB_VALUES`, không khai tay từng khoá. Bản trước
  // liệt kê `system/payment/reminder` bằng tay, nên lúc gộp tab (25/08) nó vẫn đòi khoá `payment`
  // trong khi service đã dựng object từ `NOTIFICATION_TABS` ⇒ typecheck đỏ ở `notifications.service`.
  // Bám nguồn duy nhất thì lần đổi tab sau không ai phải nhớ sửa chỗ này.
  unreadCountsByTab: z.object(
    Object.fromEntries(NOTIFICATION_TAB_VALUES.map((tab) => [tab, z.number().int().min(0)])) as Record<
      NotificationTabValue,
      z.ZodNumber
    >,
  ),
});

export type NotificationsListResponse = z.infer<typeof notificationsListResponseSchema>;

/** Response PATCH /:id/read — trả item sau khi đánh dấu đã đọc (AC3). */
export const markReadResponseSchema = notificationItemSchema;

export type MarkReadResponse = z.infer<typeof markReadResponseSchema>;

/** Response PATCH /read-all — số notification được cập nhật (AC4, idempotent). */
export const markAllReadResponseSchema = z.object({
  updatedCount: z.number().int().min(0),
});

export type MarkAllReadResponse = z.infer<typeof markAllReadResponseSchema>;
